import {FormControl} from 'platform/components';
import {Box, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {useRef, useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useRenderCustomerEmailReplyMutation,
  useRenderCustomerMessageEmailTemplateMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';

import {ApiError, Nullish, useDebouncedCallback, useDeepCompareEffect} from 'shared/web';

import {handleApiError} from '../../../utils/handleApiError';
import {isEmailHtmlEmpty} from '../../../utils/isEmailHtmlEmpty';
import {toEmailBodyPayload} from '../../../utils/toEmailBodyPayload';
import {ReplyTarget} from '../types/ReplyTarget';
import {FormValues} from '../types/SendCustomerEmailFormSchema';
import {EmailWindow} from './EmailWindow';

interface EmailPreviewProps {
  formApi: UseFormReturn<FormValues>;
  control: FormControl<FormValues>;
  reply?: ReplyTarget | Nullish;
}

// A cleared editor holds `<p><br></p>`, which `isNilOrEmpty` reports as filled.
const hasMissingPreviewData = (
  reply: ReplyTarget | Nullish,
  customerId: FormValues['customerId'],
  body: FormValues['body'],
  subject: FormValues['subject']
) =>
  isNotNil(reply)
    ? isNilOrEmpty(subject)
    : isNilOrEmpty(customerId) || (isEmailHtmlEmpty(body) && isNilOrEmpty(subject));

export function EmailPreview(props: EmailPreviewProps) {
  const [renderEmail, {isLoading: isLoadingEmail}] =
    useRenderCustomerMessageEmailTemplateMutation();
  const [renderReply, {isLoading: isLoadingReply}] = useRenderCustomerEmailReplyMutation();

  const [htmlData, setHtmlData] = useState<string | null>(null);
  const renderVersionRef = useRef<number>(0);

  const data = useWatch({
    control: props.control,
    name: ['customerId', 'body', 'subject'],
  });

  const isRequiredDataMissing = hasMissingPreviewData(props.reply, data[0], data[1], data[2]);

  const handleRenderEmail = () => {
    const version = ++renderVersionRef.current;
    const formValues = props.formApi.getValues();

    if (
      hasMissingPreviewData(props.reply, formValues.customerId, formValues.body, formValues.subject)
    ) {
      setHtmlData(null);
      return;
    }

    // The preview must agree with the send path.
    const {body, bodyFormat} = toEmailBodyPayload(formValues.body?.toString());

    const request = isNotNil(props.reply)
      ? renderReply({
          renderCustomerEmailReplyRequestBody: {
            body,
            bodyFormat,
            emailMessageId: props.reply.emailMessageId,
            customerId: formValues.customerId,
            subject: formValues.subject?.toString() ?? '',
          },
        })
      : renderEmail({
          customerMessageId: formValues.customerMessageId?.[0] ?? 'customer-message',
          renderCustomerMessageRequestBody: {
            customerMessageId: formValues.customerMessageId?.[0] ?? 'customer-message',
            customerId: formValues.customerId,
            bodyFormat,
            payload: {body, subject: formValues.subject?.toString() ?? ''},
          },
        });
    request
      .unwrap()
      .then((data) => {
        if (version === renderVersionRef.current) {
          setHtmlData(data.body);
        }
      })
      .catch((error: ApiError) => {
        if (version === renderVersionRef.current) {
          setHtmlData(null);
          handleApiError(error);
        }
      });
  };

  const debouncedHandleRenderEmail = useDebouncedCallback(handleRenderEmail, 1000);

  useDeepCompareEffect(() => {
    if (isRequiredDataMissing) {
      renderVersionRef.current++;
      setHtmlData(null);
      return;
    }

    debouncedHandleRenderEmail();
  }, [data]);

  return (
    <VStack height="100%">
      <Box position="relative" height="100%" minHeight={[75, 100]}>
        <Show when={isNilOrEmpty(htmlData)}>
          <EmailPreviewButtonWrapper data-testid="customerEmailForm-email-window-empty-preview">
            <VStack align="center">
              <Text size="xSmall" align="center" color="tertiary">
                {i18n.t('entity.customer.labels.fillAllRequiredFields')}
              </Text>
            </VStack>
          </EmailPreviewButtonWrapper>
        </Show>
        <EmailWindow
          htmlData={htmlData}
          isRerendering={(isLoadingEmail || isLoadingReply) && isNotNilOrEmpty(htmlData)}
          data-testid="customerEmailForm-email-window"
        />
      </Box>
    </VStack>
  );
}

const EmailPreviewButtonWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
